import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ShieldCheck, Building2, BadgeCheck, ArrowRight, ArrowLeft } from 'lucide-react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { BrandRule } from '../../components/brand/BrandRule';

/**
 * Projects for non-Saudis — a curated hub of real-estate projects open to
 * foreign / non-Saudi investors (foreign-ownership zones, Premium Residency).
 * Net-new surface: shipped as a polished "coming soon" landing until the backed
 * filter + listings are built. Bilingual + RTL, on the shared violet theme.
 */
export default function ProjectsNonSaudisPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const features = [
    {
      icon: Building2,
      title: isRTL ? 'مشاريع مؤهَّلة للتملّك الأجنبي' : 'Foreign-ownership eligible',
      desc: isRTL
        ? 'مشاريع ووحدات ضمن المناطق التي يسمح فيها النظام بتملّك غير السعوديين.'
        : 'Projects and units inside the zones where non-Saudi ownership is permitted.',
    },
    {
      icon: BadgeCheck,
      title: isRTL ? 'إرشاد الإقامة المميّزة' : 'Premium Residency guidance',
      desc: isRTL
        ? 'فهم متطلبات الإقامة المميّزة وربطها بفرص الاستثمار العقاري المناسبة.'
        : 'Understand Premium Residency requirements and match them to the right opportunities.',
    },
    {
      icon: ShieldCheck,
      title: isRTL ? 'مطوّرون موثّقون' : 'Vetted developers',
      desc: isRTL
        ? 'كل مشروع من مطوّر موثّق على صفقات 24 مع تراخيصه ومستنداته.'
        : 'Every project from a verified Safaqat24 developer, with licences and documents.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[var(--pv-color-surface)]">
      <Navigation />

      <main id="main-content" dir={isRTL ? 'rtl' : 'ltr'} className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="relative max-w-3xl mx-auto px-4 pt-16 pb-12 text-center">
            {/* Plain status text — no pill, no pinging dot, no glow behind the
                hero (all three are stock "AI landing page" tells). */}
            <p className="text-sm font-semibold text-[#8C6A0B] dark:text-[var(--pv-color-gold-light)]">
              {isRTL ? 'قريباً' : 'Coming soon'}
            </p>

            <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[var(--pv-color-primary)]">
              {isRTL ? 'مشاريع للمستثمرين غير السعوديين' : 'Projects for non-Saudi investors'}
            </h1>
            <BrandRule className="mx-auto mt-4" />
            <p className="mx-auto mt-3 max-w-xl text-[1rem] leading-relaxed text-[var(--pv-color-text-muted)]">
              {isRTL
                ? 'منصّة مخصّصة للمشاريع العقارية المتاحة للمستثمرين من خارج المملكة - ضمن مناطق التملّك المسموح بها، مع إرشاد حول الإقامة المميّزة. نجهّزها الآن.'
                : 'A dedicated hub for real-estate projects open to investors from outside the Kingdom - within permitted ownership zones, with Premium Residency guidance. We are building it now.'}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/investors"
                className="inline-flex h-12 items-center gap-2 rounded-full bg-[var(--pv-color-primary)] px-6 text-[0.9375rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)]"
              >
                {isRTL ? 'تصفّح منصّة المستثمرين' : 'Explore the Investors Hub'}
                <Arrow className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex h-12 items-center gap-2 rounded-full border border-[var(--pv-border-strong)] px-6 text-[0.9375rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)]"
              >
                {isRTL ? 'أبلغني عند الإطلاق' : 'Notify me at launch'}
              </Link>
            </div>
          </div>
        </section>

        {/* What to expect: three short points under a hairline, no box or icon
            tile each (owner, 2026-10-07). */}
        <section className="mx-auto max-w-5xl border-t border-[var(--pv-border-subtle)] px-4 pb-20 pt-12">
          <ul role="list" className="m-0 grid list-none gap-x-10 gap-y-8 p-0 sm:grid-cols-3">
            {features.map(({ icon: Icon, title, desc }) => (
              <li key={title} className="text-start">
                <h3 className="flex items-center gap-2 text-[1.0625rem] font-semibold text-[var(--pv-color-text)]">
                  <Icon className="h-5 w-5 shrink-0 text-[var(--pv-color-primary)]" aria-hidden="true" />
                  {title}
                </h3>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">{desc}</p>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <Footer />
    </div>
  );
}
